import dayjs, { Dayjs } from 'dayjs'
import { ReactNode } from 'react'
import { ProjectStatuses, projectStatusName } from '../../common/constants'

export const soldOnFormat = 'YYYY-MM-DD'

export const parseSoldOn = (soldOn: string | null | undefined): Dayjs | null =>
  soldOn ? dayjs(soldOn) : null

export const soldOnForRequest = (sold: boolean, soldOn: Dayjs | null | undefined): string | null =>
  sold && soldOn ? soldOn.format(soldOnFormat) : null

export const soldOnAfterSoldToggle = (sold: boolean, current: Dayjs | null | undefined, today: Dayjs): Dayjs | null => {
  if (!sold) {
    return null
  }
  return current ?? today
}

export type ClockOutRequirementOption = 'companyDefault' | 'required' | 'notRequired'

export const clockOutRequirementOptions: { label: string, value: ClockOutRequirementOption }[] = [
  { label: 'Company default', value: 'companyDefault' },
  { label: 'Required', value: 'required' },
  { label: 'Not required', value: 'notRequired' },
]

export const toClockOutRequirementOption = (required: boolean | null | undefined): ClockOutRequirementOption => {
  if (required === true) {
    return 'required'
  }
  if (required === false) {
    return 'notRequired'
  }
  return 'companyDefault'
}

export const fromClockOutRequirementOption = (option: ClockOutRequirementOption | undefined): boolean | null => {
  if (option === 'required') {
    return true
  }
  if (option === 'notRequired') {
    return false
  }
  return null
}

export const clockOutRequirementForRequest = (
  option: ClockOutRequirementOption | undefined,
  saved: boolean | null | undefined
): boolean | null => option === undefined ? saved ?? null : fromClockOutRequirementOption(option)

export const toggleScheduler = (selected: string[] | undefined, email: string, notify: boolean): string[] => {
  const others = (selected ?? []).filter(x => x !== email)
  return notify ? [...others, email] : others
}

// job-details-cleanup layout only. The form keeps a job with no status as -1 (sent back as null).
export const unscheduledStatusValue = -1

export const statusOptions: { label: string, value: number }[] = Object.values(ProjectStatuses).map(status => ({
  label: projectStatusName(status),
  value: status === ProjectStatuses.Unscheduled ? unscheduledStatusValue : status,
}))

// Jobs moved to Unscheduled on the Pipeline board store 0. Display only: the stored 0 is saved back unchanged.
export const statusLabel = ({ label, value }: { label?: ReactNode, value?: string | number }): ReactNode =>
  value === ProjectStatuses.Unscheduled ? projectStatusName(ProjectStatuses.Unscheduled) : label

// Saved schedulers who are no longer active users still get the sold email, so the switch grid lists
// them too (by email) where they can be switched off.
export const schedulerSwitchOptions = (
  activeUsers: { label: string, value: string }[],
  selected: string[] | undefined
): { label: string, value: string }[] => {
  const activeEmails = new Set(activeUsers.map(x => x.value))
  const inactive = (selected ?? []).filter(email => email && !activeEmails.has(email))
  return [...activeUsers, ...inactive.map(email => ({ label: `${email} (inactive)`, value: email }))]
}
